import { Alert, Button, Collapse, Descriptions, Form, Result, Select, Space } from 'antd';
import { AxiosError } from 'axios';

import { Auth, Azure, Rules } from '@app/web-components';

import { useBundleViewPanel, useFindBundleById } from '../../bundles';
import { EmployeeTypeRenderer } from '../../common';
import { useSubscriptionSelectAccess } from '../atoms';
import { useCreateSubscription, useFindWorker } from '../data';
import { EvidenceModal } from '../modals';

import {
  JustificationTooltipText,
  SelectAccessTooltipText,
  useBundleSelect,
  useJustificationFormItem,
  useSelectAccessesFormItem,
} from './inputs';
import { useCreateSubscriptionValidations } from './rules';

type FormData = {
  bundleId: number;
  bundleAccessIds: number[];
  worker?: Azure.WorkerOption; // only populated if another worker is selected
  justification?: string;
};

export type CreateFormProps = {
  bundleId: number | undefined;
  onChange: (e: { bundleId?: number }) => void;
};

const BundleTooltipText = () => (
  <>
    The <strong>Bundle</strong> is the collection of accesses that can be granted to the worker. When you select a
    bundle, the details will appear below the bundle field.
    <ul>
      <li>A bundle must be selected.</li>
      <li>The bundle's employee type must match the selected worker's employee type.</li>
    </ul>
  </>
);

const WorkerTooltipText = () => (
  <>
    The <strong>Worker</strong> is the user who will be subscribed. You can click "Assign to Me" to assign to yourself
    or you can search for other workers by name or WWID. A worker must be selected.
  </>
);

export const CreateForm = ({ bundleId, onChange }: CreateFormProps) => {
  const { navigate, subscriptionIsUniqueForUserRule, workerTypeMatchesBundleTypeRule } =
    useCreateSubscriptionValidations();
  const { user } = Auth.useUserAuth();
  const [form] = Form.useForm();
  const createSubscription = useCreateSubscription({});
  const selectedBundleId = Form.useWatch('bundleId', form);
  const selectedWorker = Form.useWatch('worker', form);

  const workerQuery = useFindWorker(selectedWorker?.value);

  const bundleQuery = useFindBundleById({
    bundleId: selectedBundleId || bundleId || 0,
    enabled: !!selectedBundleId || !!bundleId,
  });

  const { getSelectAccessTableProps: props, selectAccessTourStepProps } = useSubscriptionSelectAccess(
    selectedBundleId,
    selectedWorker?.value,
    form,
  );
  const { getProps: getBundleSelectProps } = useBundleSelect({ employeeType: workerQuery.data?.employeeType });
  const { getProps: getJustificationFormItemProps } = useJustificationFormItem(bundleQuery.data);
  const { getProps: getSelectAccessesFormItemProps } = useSelectAccessesFormItem({
    props,
    form,
    selectAccessTourStepProps,
  });
  const { getPanelSpaceProps } = useBundleViewPanel(selectedBundleId || bundleId);

  if (bundleQuery.error) {
    const error = bundleQuery.error as AxiosError;

    if (error.response?.status === 404) {
      return <Result status="404" title="This bundle does not exist or was deleted." />;
    }

    return <Result status="error" title="Something went wrong getting the bundle." />;
  }

  if (!bundleQuery.isPending && bundleQuery.data && !bundleQuery.data.isActive) {
    return (
      <Result
        status="error"
        title="Invalid bundle subscription"
        subTitle={`Cannot subscribe to inactive bundle ${bundleQuery?.data?.name}`}
        extra={[
          <Button type="primary" key="go to subscriptions" onClick={() => navigate('../')}>
            Go To Subscriptions
          </Button>,
        ]}
      />
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChangedValues = (changedValues: any) => {
    if (changedValues.worker || changedValues.bundleId) {
      // keep our parent up to date so it can update the url search params as the form changes
      if (changedValues.bundleId) {
        onChange({
          bundleId: changedValues.bundleId || bundleId,
        });
      }
      // Reset bundleAccessIds if either worker or bundleId has changed
      form.setFieldsValue({
        bundleAccessIds: [],
      });
    }
  };

  const handleResetForm = () => {
    form.resetFields();
    createSubscription.reset();
  };

  const handleSubmit = (data: FormData) => {
    createSubscription.mutate({
      bundleId: data.bundleId,
      bundleAccessIds: data.bundleAccessIds,
      enterpriseId: data.worker?.value ?? '',
      justification: data.justification,
    });
  };

  if (createSubscription.isSuccess && createSubscription.data.data.isSuccess) {
    return (
      <Result
        status="success"
        title="Successfully subscribed to a bundle!"
        subTitle={`Bundle name: ${createSubscription.data.data.mutated?.bundle.name} for user: ${createSubscription.data.data.mutated?.user.name}`}
        extra={[
          <Button type="primary" key="go to subscriptions" onClick={() => navigate('../')}>
            Go To Subscriptions
          </Button>,
          <Button
            key="subscribe to another"
            onClick={() => {
              createSubscription.reset();
              form.resetFields();
              onChange({ bundleId });
            }}
          >
            Subscribe To Another
          </Button>,
        ]}
      />
    );
  }

  const instructions = [
    {
      key: '1',
      label: 'Instructions',
      children: (
        <>
          <p>Subscriptions are used to grant the accesses within the selected bundle for a worker.</p>
          <p>
            <WorkerTooltipText />
          </p>
          <p>
            <BundleTooltipText />
          </p>
          <p>
            The selected worker and bundle combination should not already exist. If it does, an error message will
            appear that will link you to the existing subscription. When both worker and bundle are selected, you will
            be given the ability to <strong>Select Accesses</strong> under the bundle to subscribe to.
            <SelectAccessTooltipText
              paragraphs={
                <>
                  <p>
                    According to the worker and bundle selected, the application will enable or disable the bundle items
                    based on validations. The result of the validations will be displayed in the Eligibility column. To
                    view detailed validation results, hover over the eligibility result tag for that line item.
                  </p>
                  <p>
                    Since the validations are related to DMS, product family, DIE, process, etc, the best channel to
                    ensure if they really should have access to this specific item is to revisit with the CW BB Sponsor
                    or Manager.
                  </p>
                  <p>
                    For more information on the validations: Blue Badge users (Intel employees)&nbsp;
                    <a
                      href="https://wiki.ith.intel.com/pages/viewpage.action?pageId=3040657977"
                      target="_blank"
                      rel="noreferrer"
                    >
                      click here
                    </a>
                    &nbsp; and Green Badge users (Contingent Workers)&nbsp;
                    <a
                      href="https://wiki.ith.intel.com/pages/viewpage.action?pageId=3485306786"
                      target="_blank"
                      rel="noreferrer"
                    >
                      click here
                    </a>
                    .
                  </p>
                  <p>
                    Subscribers should request access to only the bundle items needed to perform their work. User can
                    select item per item or all the enabled for the specific person by clicking in the top selection
                    box.
                  </p>
                </>
              }
            />
          </p>
          <p>
            <JustificationTooltipText />
          </p>
          <p>
            Please note that SAAF is not replacing AGS or IEM, these are just underlying applications to create the
            bundles, hence none of their regular functionalities, conditions, approval workflows or anything else is
            modified, removed or changed by SAAF. This means SAAF will trigger the bundle item request to those tools.
          </p>
        </>
      ),
    },
  ];

  return (
    <Form
      form={form}
      layout="vertical"
      style={{ width: '100%', minWidth: 1000 }}
      disabled={createSubscription.isPending || createSubscription.isSuccess}
      onFinish={handleSubmit}
      initialValues={{
        bundleId,
      }}
      requiredMark
      onValuesChange={handleChangedValues}
    >
      {createSubscription.error ? (
        <Alert
          message="Error"
          description={`Something went wrong. Error: ${createSubscription.error}`}
          type="error"
          showIcon
          style={{ marginBottom: 20 }}
        />
      ) : undefined}
      {createSubscription.isSuccess && !createSubscription.data.data.isSuccess ? (
        <Alert
          message="Sorry, subscribing to this bundle is not possible."
          description={<Rules.RuleMessagesPanel messages={createSubscription.data.data.messages} />}
          type="error"
          showIcon
          style={{ marginBottom: 20 }}
        ></Alert>
      ) : undefined}
      <Collapse items={instructions} style={{ marginBottom: '1em' }} />
      <Form.Item
        label="Worker"
        name="worker"
        rules={[{ required: true, message: 'Worker is required.' }]}
        tooltip={<WorkerTooltipText />}
      >
        <Azure.WorkerPicker placeholder="Search Intel workers by name or WWID" />
      </Form.Item>
      {workerQuery.data ? (
        <Descriptions
          bordered
          column={2}
          items={[
            { key: 'wwid', label: 'WWID', children: workerQuery.data.enterpriseId },
            {
              key: 'email',
              label: 'Email',
              children: <a href={`mailto:${workerQuery.data.email}`}>{workerQuery.data.email}</a>,
            },
            { key: 'idsid', label: 'IDSID', children: workerQuery.data.idsid },
            {
              key: 'employee-type',
              label: 'Employee Type',
              children: <EmployeeTypeRenderer employeeType={workerQuery.data.employeeType} />,
            },
            ...(user?.isSystemAdmin
              ? [
                  {
                    key: 'evidence',
                    label: 'SYSTEM ADMIN ONLY',
                    children: <EvidenceModal enterpriseId={selectedWorker?.value} />,
                  },
                ]
              : []),
          ]}
          size="small"
          style={{ marginBottom: '1.5em' }}
        />
      ) : undefined}
      <Form.Item
        label="Bundle"
        name="bundleId"
        dependencies={['worker']}
        rules={[
          { required: true, message: 'Bundle is required.' },
          ({ getFieldValue }) => ({
            validator: workerTypeMatchesBundleTypeRule(getFieldValue('worker')),
          }),
          ({ getFieldValue }) => ({
            validator: subscriptionIsUniqueForUserRule(getFieldValue('worker')),
          }),
        ]}
        tooltip={<BundleTooltipText />}
      >
        <Select {...getBundleSelectProps()} />
      </Form.Item>
      {bundleId ? <Space {...getPanelSpaceProps({ style: { marginBottom: '1.5em' } })} /> : undefined}
      <Form.Item {...getSelectAccessesFormItemProps()} />
      <Form.Item {...getJustificationFormItemProps()} />
      <Space style={{ marginTop: 20 }}>
        <Button type="primary" htmlType="submit" loading={createSubscription.isPending}>
          Submit
        </Button>
        <Button onClick={handleResetForm}>Reset</Button>
        <Button onClick={() => navigate('../')}>Cancel</Button>
      </Space>
    </Form>
  );
};
